import { Eye, EyeOff, Search, X } from "lucide-react";
import { useState, type InputHTMLAttributes, type ReactNode, type Ref } from "react";
import { cn } from "../lib/cn";
import { useUiText } from "../lib/locale";
import { controlSize, fieldShell, type ControlSize } from "../lib/variants";
import { useField } from "./field";

export type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "prefix"> & {
  ref?: Ref<HTMLInputElement>;
  size?: ControlSize;
  invalid?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Показывает кнопку очистки, когда есть значение. */
  onClear?: () => void;
  wrapperClassName?: string;
};

export function Input({
  size = "md", invalid, leftIcon, rightIcon, prefix, suffix, onClear, className, wrapperClassName, disabled, id, ...props
}: InputProps) {
  const field = useField();
  const text = useUiText();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const isDisabled = disabled ?? field?.disabled;
  const hasValue = props.value !== undefined && props.value !== "" && props.value !== null;
  return (
    <div
      data-invalid={isInvalid}
      data-disabled={!!isDisabled}
      className={cn(fieldShell, controlSize({ size }), "[&_svg]:shrink-0 [&_svg]:text-fg", wrapperClassName)}
    >
      {leftIcon}
      {prefix && <span className="shrink-0 text-muted">{prefix}</span>}
      <input
        id={id ?? field?.id}
        disabled={isDisabled}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        className={cn("h-full min-w-0 flex-1 bg-transparent outline-none placeholder:text-placeholder disabled:cursor-not-allowed", className)}
        {...props}
      />
      {onClear && hasValue && !isDisabled && (
        <button type="button" aria-label={text.clear} onClick={onClear} className="-me-1 grid size-7 place-items-center rounded-full text-muted hover:bg-surface-2 [&_svg]:size-5! [&_svg]:text-muted!">
          <X />
        </button>
      )}
      {suffix && <span className="shrink-0 text-muted">{suffix}</span>}
      {rightIcon}
    </div>
  );
}

export function PasswordInput(props: Omit<InputProps, "type" | "rightIcon">) {
  const [visible, setVisible] = useState(false);
  const text = useUiText();
  return (
    <Input
      type={visible ? "text" : "password"}
      autoComplete="current-password"
      {...props}
      rightIcon={
        <button type="button" aria-label={visible ? text.hidePassword : text.showPassword} onClick={() => setVisible((v) => !v)} className="-me-1 grid size-8 place-items-center rounded-full hover:bg-surface-2">
          {visible ? <EyeOff /> : <Eye />}
        </button>
      }
    />
  );
}

export type SearchInputProps = Omit<InputProps, "type" | "leftIcon" | "onChange" | "value"> & {
  value: string;
  onValueChange: (value: string) => void;
};

export function SearchInput({ value, onValueChange, placeholder, ...props }: SearchInputProps) {
  const text = useUiText();
  return (
    <Input
      type="search"
      inputMode="search"
      enterKeyHint="search"
      placeholder={placeholder ?? text.search}
      leftIcon={<Search />}
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      onClear={() => onValueChange("")}
      className="[&::-webkit-search-cancel-button]:hidden"
      {...props}
    />
  );
}
